/**
 * DOM helpers that enforce max length on contenteditable elements.
 * Pure length math lives in canvasUtils — this file only touches the Selection API.
 */

import {
  clampTextLength,
  contentEditableLength,
  remainingContentCapacity,
} from './canvasUtils';

/**
 * Block / truncate inserts that would exceed `maxLength`.
 * Call from React `onBeforeInput` with the native InputEvent.
 */
export function handleContentEditableBeforeInput(
  e: InputEvent,
  el: HTMLElement,
  maxLength: number,
): void {
  const inputType = e.inputType ?? '';
  // Always allow deletions and undo/redo.
  if (inputType.startsWith('delete') || inputType.startsWith('history')) return;

  const current = contentEditableLength(el.innerText ?? '');
  const selected = window.getSelection()?.toString().length ?? 0;
  const data = e.data ?? e.dataTransfer?.getData('text/plain') ?? '';
  // insertParagraph / insertLineBreak add a newline without `data`.
  const incomingLength = data.length > 0 ? data.length : (inputType.startsWith('insert') ? 1 : 0);
  if (incomingLength === 0) return;

  const room = remainingContentCapacity(current, selected, maxLength);
  if (incomingLength <= room) return;

  e.preventDefault();
  if (room === 0) return;

  const toInsert = data.length > 0 ? data.slice(0, room) : '\n';
  // insertText is the reliable way to insert while preserving the undo stack.
  document.execCommand('insertText', false, toInsert);
}

/**
 * Safety net after input/paste: if the editor somehow exceeds `maxLength`,
 * truncate and place the caret at the end. Returns true when truncation ran.
 */
export function truncateContentEditableIfNeeded(
  el: HTMLElement,
  maxLength: number,
): boolean {
  const raw = el.innerText ?? '';
  const text = raw === '\n' ? '' : raw;
  if (text.length <= maxLength) return false;

  el.innerText = clampTextLength(text, maxLength);
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel?.removeAllRanges();
  sel?.addRange(range);
  return true;
}
